import {
  ApolloQueryResult,
  OperationVariables,
} from '@apollo/client';
import { IProps } from '@components/messages/types';
import React, {useEffect, useState } from 'react';
import { useLazyGetAllMessageGroupsQuery } from '../queries';
import { 
  GetAllMessageGroups, 
  GetAllMessageGroupsVariables, 
  GetAllMessageGroups_getAllMessageGroups_page_edges 
} from '../types/GetAllMessageGroups';
import useOVSPagination from 'hooks/useOVSPagination';
import { useDebouncedEffect } from 'utils/useDebouncedEffect';
import { funNumberAgr } from '@types';

export interface IMessageGroupContext {
  items: GetAllMessageGroups_getAllMessageGroups_page_edges[];
  setItems: (
    itemSKUs: GetAllMessageGroups_getAllMessageGroups_page_edges[]
  ) => void;
  loading: boolean;
  refetchItems:
    | ((
        variables?: Partial<OperationVariables> | undefined
      ) => Promise<ApolloQueryResult<GetAllMessageGroups>>)
    | undefined;
  getItems: () => void;

  goTo: (nextPrev: boolean) => void;
  paging: any;

  setSearchQuery: (query: string) => void;
  searchQuery: string | undefined;
  setPageLimit: funNumberAgr
}

export const MessageGroupContext = React.createContext<IMessageGroupContext>(
  {} as IMessageGroupContext
);

const MessageGroupProvider: React.FC<IProps> = ({ children }) => {
  const [items, setItems] = React.useState<
  GetAllMessageGroups_getAllMessageGroups_page_edges[]
  >([] as GetAllMessageGroups_getAllMessageGroups_page_edges[]);
  const [loading] = React.useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);

  const {
    endCursorStack,
    initPagAction,
    setPaging,
    paging, pageListLimit, setPageListLimit
  } = useOVSPagination();


  const [
    getItems,
    { refetch: refetchItems, fetchMore, data, loading: dataLoading },
  ] = useLazyGetAllMessageGroupsQuery({
    first: pageListLimit,
  });

  useEffect(() => {
    if (data && !dataLoading) {
      setItems(data?.getAllMessageGroups?.page?.edges || items);
      setPaging({
        ...data?.getAllMessageGroups?.pageData,
        ...data?.getAllMessageGroups?.page?.pageInfo,
      });
    } // eslint-disable-next-line
  }, [data]);
  const setPageLimit = (limit: number) => {
    setPageListLimit(limit)
    setTimeout(() => {
      refetchItems && refetchItems()
    }, 100);

  }
  const goTo = async (next = true) => {
    if (isLoadingMore) return;

    let variables: GetAllMessageGroupsVariables = {
      first: pageListLimit,
      ...initPagAction(next),
    };

    if (fetchMore) {
      if (searchQuery) {
        variables['search'] = searchQuery;
      }
      setIsLoadingMore(true);
      const _data: { data: GetAllMessageGroups } = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllMessageGroups?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllMessageGroups?.pageData,
        ..._data?.data?.getAllMessageGroups?.page?.pageInfo,
      });
    }
  };

  const search = async () => {
    if (searchQuery === undefined) {
      return;
    }
    if (fetchMore) {
      const variables: GetAllMessageGroupsVariables = {
        first: pageListLimit,
      };
      if (searchQuery) {
        variables['search'] = searchQuery;
      }
      setIsLoadingMore(true);
      const _data: { data: GetAllMessageGroups } = await fetchMore({
        variables,
        updateQuery: (previousResult, { fetchMoreResult }) => {
          setIsLoadingMore(false);
          if (!fetchMoreResult) {
            return previousResult;
          }
          return {
            ...fetchMoreResult,
          };
        },
      });

      setItems(_data?.data?.getAllMessageGroups?.page?.edges || []);

      setPaging({
        ..._data?.data?.getAllMessageGroups?.pageData,
        ..._data?.data?.getAllMessageGroups?.page?.pageInfo,
        hasPreviousPage: endCursorStack.length > 0,
      });
    }
  };

  useDebouncedEffect(search, [searchQuery], 1000);

  const value = React.useMemo(
    () => ({
      items,
      setItems,
      loading: loading || dataLoading,
      refetchItems,
      getItems,
      goTo,
      paging,
      setSearchQuery,
      searchQuery, setPageLimit,
    }), // eslint-disable-next-line
    [items, paging, searchQuery]
  );

  return (
    <MessageGroupContext.Provider value={value}>
      {children}
    </MessageGroupContext.Provider>
  );
};

export default MessageGroupProvider;
